/**
 * useInfiniteList — generic cursor-paginated list fetcher.
 *
 * Backend contract:
 *   GET <endpoint>?cursor=<str|null>&limit=<int>&<...params>
 *   →  { items: [...], next_cursor: <str|null>, total_estimate?: <int> }
 *
 * On scroll-to-bottom (or manual `loadMore()`), the next page is appended.
 * `refresh()` resets cursor and refetches from scratch.
 *
 * Params are passed through opaquely. Changing params resets the list.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const buildQS = (params, cursor, limit) => {
  const usp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    usp.set(k, String(v));
  });
  usp.set('limit', String(limit));
  if (cursor) usp.set('cursor', cursor);
  return usp.toString();
};

export const useInfiniteList = (endpoint, params = {}, opts = {}) => {
  const { pageSize = 50, authHeaders = () => ({}) } = opts;
  // Stable param key so we only refetch when meaningful inputs change.
  const paramsKey = useMemo(() => JSON.stringify(params), [params]);

  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [totalEstimate, setTotalEstimate] = useState(null);
  const inflight = useRef(false);

  const fetchPage = useCallback(async (nextCursor, append) => {
    if (inflight.current) return;
    inflight.current = true;
    if (append) setIsLoadingMore(true); else setIsLoading(true);
    setError(null);
    try {
      const qs = buildQS(JSON.parse(paramsKey), nextCursor, pageSize);
      const r = await axios.get(`${API}${endpoint}?${qs}`, { headers: authHeaders() });
      const page = r.data || {};
      setItems((prev) => (append ? [...prev, ...(page.items || [])] : (page.items || [])));
      setCursor(page.next_cursor ?? null);
      setHasMore(Boolean(page.next_cursor));
      if (page.total_estimate != null) setTotalEstimate(page.total_estimate);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      inflight.current = false;
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [endpoint, paramsKey, pageSize, authHeaders]);

  // Reset on param change
  useEffect(() => {
    setItems([]);
    setCursor(null);
    setHasMore(true);
    fetchPage(null, false);
  }, [paramsKey, endpoint]);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading || isLoadingMore) return;
    fetchPage(cursor, true);
  }, [hasMore, isLoading, isLoadingMore, cursor, fetchPage]);

  const refresh = useCallback(() => fetchPage(null, false), [fetchPage]);

  return { items, isLoading, isLoadingMore, hasMore, error, totalEstimate, loadMore, refresh };
};

export default useInfiniteList;
